// Client-side auth: discovers the server's auth mode, manages the stored
// session token, and produces the auth headers attached to every API request.
//
// SSO is server-side OIDC (like legacy ATLAS): the SPA never talks to Microsoft
// or MSAL directly — it just navigates to the server's /api/auth/sso/login,
// which bounces through Microsoft and redirects back with a minted session
// token in the URL fragment. So every real-auth mode (email + sso) ends up
// carrying the SAME self-issued session bearer token.
//
// Token precedence in getAuthHeaders():
//   1. a self-issued session token (magic-link OR SSO callback),
//   2. the dev persona header (dev mode only).

export type AuthMode = 'dev' | 'email' | 'sso';

export interface AuthConfig {
  mode: AuthMode;
  emailLoginEnabled: boolean;
  /** Emergency username/password sign-in — offer the password form when true. */
  passwordLoginEnabled: boolean;
  enforceSso: boolean;
  /** Present in SSO mode. `ready` is false until a client secret is stored. */
  sso: { loginUrl: string; ready: boolean } | null;
}

const SESSION_KEY = 'atlas.session'; // self-issued session JWT (magic-link / SSO)
const USER_KEY = 'atlas.user'; // active identity email (dev persona + signed-in user)

// --- Persona / active-user email (back-compat: re-exported from lib/api) -------
export function getActiveUser(): string {
  return localStorage.getItem(USER_KEY) ?? 'admin@tracx.local';
}
export function setActiveUser(email: string): void {
  localStorage.setItem(USER_KEY, email);
}

// --- Session token -------------------------------------------------------------
export function getSessionToken(): string | null {
  return localStorage.getItem(SESSION_KEY);
}
export function setSessionToken(token: string): void {
  localStorage.setItem(SESSION_KEY, token);
}
export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
}

// --- Server auth config (cached) ----------------------------------------------
let configPromise: Promise<AuthConfig> | null = null;
export function loadAuthConfig(): Promise<AuthConfig> {
  if (!configPromise) {
    configPromise = fetch('/api/v2/auth/config')
      .then((r) => r.json())
      .then((p) => p.data as AuthConfig)
      .catch(() => ({ mode: 'dev', emailLoginEnabled: false, passwordLoginEnabled: false, enforceSso: false, sso: null }) as AuthConfig);
  }
  return configPromise;
}

// --- Microsoft SSO (server-side OIDC) -----------------------------------------
/** Begin Microsoft sign-in: full-page navigation to the server login route. */
export function startSsoLogin(loginUrl: string): void {
  const returnTo = window.location.pathname + window.location.search;
  window.location.assign(`${loginUrl}?return_to=${encodeURIComponent(returnTo)}`);
}

/**
 * If the SSO callback landed us back with `#sso_token=…`, pull it out (and strip
 * it from the URL). Returns the token, or null when there's none.
 */
export function consumeSsoTokenFromHash(): string | null {
  if (!window.location.hash.startsWith('#')) return null;
  const params = new URLSearchParams(window.location.hash.slice(1));
  const token = params.get('sso_token');
  if (!token) return null;
  window.history.replaceState({}, '', window.location.pathname + window.location.search);
  return token;
}

// --- Per-request headers + sign-out -------------------------------------------
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const session = getSessionToken();
  if (session) return { authorization: `Bearer ${session}` };

  const cfg = await loadAuthConfig();
  if (cfg.mode === 'dev') return { 'x-atlas-user': getActiveUser() };
  // email + sso: the session token (handled above) is the only credential.
  return {};
}

export async function signOut(): Promise<void> {
  clearSession();
  window.location.assign('/');
}
